import { ColumnDef } from "@tanstack/react-table";
import { Visit } from "../types/visit";
import { Badge } from "../../../components/ui/badge";
import { format } from "date-fns";
import { MoreHorizontal, FileText, Printer, Calendar, Circle, PackageX } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../../../components/ui/dropdown-menu";
import { Button } from "../../../components/ui/button";
import { DataTable } from "../../../components/ui/data-table";
import { useVisitHistory } from "../hooks/useVisitHistory";
import { useState } from "react";
import { VisitDetailsDialog } from "./VisitDetailsDialog";
import { PrintVisitDialog } from "./PrintVisitDialog";
import { TableActionCell } from "../../../components/ui/table";
import { cn } from "../../../lib/utils";

interface PatientVisitHistoryProps {
  patientId: string;
}

const statusStyles = {
  completed: {
    color: "text-emerald-700 dark:text-emerald-500",
    bg: "bg-emerald-50 dark:bg-emerald-950",
    border: "border-emerald-200/50 dark:border-emerald-900/50",
    icon: "text-emerald-500 dark:text-emerald-400"
  },
  "in-progress": {
    color: "text-blue-700 dark:text-blue-500",
    bg: "bg-blue-50 dark:bg-blue-950",
    border: "border-blue-200/50 dark:border-blue-900/50",
    icon: "text-blue-500 dark:text-blue-400"
  },
  scheduled: {
    color: "text-amber-700 dark:text-amber-500",
    bg: "bg-amber-50 dark:bg-amber-950",
    border: "border-amber-200/50 dark:border-amber-900/50",
    icon: "text-amber-500 dark:text-amber-400"
  },
  cancelled: {
    color: "text-rose-700 dark:text-rose-500",
    bg: "bg-rose-50 dark:bg-rose-950",
    border: "border-rose-200/50 dark:border-rose-900/50",
    icon: "text-rose-500 dark:text-rose-400"
  }
};

export function PatientVisitHistory({ patientId }: PatientVisitHistoryProps) {
  const { data: visits, isLoading } = useVisitHistory({ patientId });
  const [selectedVisit, setSelectedVisit] = useState<Visit | null>(null);
  const [isDetailsOpen, setIsDetailsOpen] = useState(false);
  const [isPrintOpen, setIsPrintOpen] = useState(false);

  const columns: ColumnDef<Visit>[] = [
    {
      accessorKey: "startTime",
      header: "Date & Time",
      cell: ({ row }) => {
        const date = new Date(row.getValue("startTime"));
        return (
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center">
              <Calendar className="w-4 h-4 text-primary/70" />
            </div>
            <div className="flex flex-col">
              <span className="font-medium">{format(date, "MMM d, yyyy")}</span>
              <span className="text-sm text-muted-foreground">{format(date, "h:mm a")}</span>
            </div>
          </div>
        );
      },
    },
    {
      accessorKey: "type",
      header: "Type",
      cell: ({ row }) => {
        const type = row.getValue("type") as string;
        return (
          <Badge 
            variant={type === "outreach" ? "secondary" : "default"}
            className="capitalize font-medium"
          >
            {type}
          </Badge>
        );
      },
    },
    {
      accessorKey: "status",
      header: "Status",
      cell: ({ row }) => {
        const status = row.getValue("status") as keyof typeof statusStyles;
        const style = statusStyles[status];
        
        return (
          <div className={cn(
            "flex items-center gap-2 w-fit px-2.5 py-1 rounded-full border",
            style.bg,
            style.border
          )}>
            <Circle className={cn("w-2 h-2", style.icon)} />
            <span className={cn("text-sm font-medium capitalize", style.color)}>
              {status}
            </span>
          </div>
        );
      },
    },
    {
      accessorKey: "diagnosis",
      header: "Diagnosis",
      cell: ({ row }) => (
        <span className="text-muted-foreground">
          {row.getValue("diagnosis") || "-"}
        </span>
      ),
    },
    {
      id: "actions",
      cell: ({ row }) => {
        const visit = row.original;

        return (
          <TableActionCell>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button 
                  variant="ghost" 
                  size="icon"
                  className="hover:bg-primary/10 data-[state=open]:bg-primary/10"
                >
                  <MoreHorizontal className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent 
                align="end" 
                className="w-52 bg-background/80 backdrop-blur-sm"
              >
                <DropdownMenuLabel className="text-muted-foreground">
                  Visit Actions
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem 
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelectedVisit(visit);
                    setIsDetailsOpen(true);
                  }}
                  className="cursor-pointer hover:bg-primary/10"
                >
                  <FileText className="mr-2 h-4 w-4" /> View Details
                </DropdownMenuItem>
                <DropdownMenuItem 
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelectedVisit(visit);
                    setIsPrintOpen(true);
                  }}
                  className="cursor-pointer hover:bg-primary/10"
                >
                  <Printer className="mr-2 h-4 w-4" /> Print Record
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </TableActionCell>
        );
      },
    },
  ];

  if (visits?.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-12 px-4 border rounded-lg bg-background/50 backdrop-blur-sm">
        <div className="h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center mb-4">
          <PackageX className="h-6 w-6 text-primary/70" />
        </div>
        <h3 className="text-lg font-medium text-foreground mb-1">No Visit History</h3>
        <p className="text-sm text-muted-foreground text-center max-w-sm">
          This patient doesn't have any recorded visits yet.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="rounded-lg border border-border/50 bg-background/50 backdrop-blur-sm shadow-md">
        <DataTable
          columns={columns}
          data={visits || []}
          isLoading={isLoading}
          rowSize="sm"
        />
      </div>

      {selectedVisit && (
        <>
          <VisitDetailsDialog
            visit={selectedVisit}
            open={isDetailsOpen}
            onOpenChange={setIsDetailsOpen}
          />
          <PrintVisitDialog
            visit={selectedVisit}
            open={isPrintOpen}
            onOpenChange={setIsPrintOpen}
          />
        </>
      )}
    </div>
  );
}
